import React, { ReactElement } from 'react';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import { Translations } from './configuration';
import { Typography } from '@material-ui/core';
import {
  AppSelect,
  AppTextField,
  AppSwitch,
  AppDatePicker,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { SchemeTypes } from '../../../../config';
import { getState } from '../../../../redux/store';
import { ReducerKeys } from '../../../../redux/reducers';
import { FormFields } from '../../configuration';

interface IProps {
  mode: 'VIEW' | 'EDIT';
  errors: DeepMap<FormFields, FieldError>;
  control: Control<FormFields>;
}

interface IElementsProps {
  size: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  element: ReactElement;
  id: string;
  rowStyle?: any;
}

const renderVatReturnYear = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT'
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      fullWidth
      id="vatReturnPeriodDetails_year"
      label={
        <TranslatedText textMap={Translations.vat_return_year_control} />
      }
      error={'vatReturnPeriodDetails_year' in errors}
      name="vatReturnPeriodDetails_year"
      control={control}
      rules={{}}
      helperText={
        errors.vatReturnPeriodDetails_year &&
        errors.vatReturnPeriodDetails_year.message
      }
      disabled={true}
    />
  );
};

const renderVatReturnVersion = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT'
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      fullWidth
      id="vatReturnPeriodDetails_vatReturnVersion"
      label={
        <TranslatedText textMap={Translations.vat_return_version_control} />
      }
      error={'vatReturnPeriodDetails_vatReturnVersion' in errors}
      name="vatReturnPeriodDetails_vatReturnVersion"
      control={control}
      rules={{}}
      helperText={
        errors.vatReturnPeriodDetails_vatReturnVersion &&
        errors.vatReturnPeriodDetails_vatReturnVersion.message
      }
      disabled={true}
    />
  );
};

export default function VatReturnPeriodDetails(props: IProps): ReactElement {
  // console.log('Render VatReturnPeriodDetails');
  const mode = props.mode;
  // const SCHEME_TYPE = props.scheme_type;
  // const IS_AGENT = props.is_agent;
  const errors = props.errors;
  const control = props.control;
  // const isExcluded = props.isExcluded;

  function handleFormElements(): IElementsProps[] {
    return [
      {
        element: renderVatReturnPeriod(errors, control, mode),
        size: 6,
        id: 'vatReturnPeriod',
      },
      {
        element: renderVatReturnYear(errors, control, mode),
        size: 6,
        id: 'vatReturnYear',
      },
      {
        element: renderVatReturnSubmissionDate(errors, control, mode),
        size: 6,
        id: 'vatReturnSubmissionDate',
      },
      {
        element: renderVatReturnVersion(errors, control, mode),
        size: 6,
        id: 'vatReturnVersion',
      },
      {
        element: renderStartDateField(errors, control, mode),
        size: 6,
        id: 'startDate',
      },
      {
        element: renderEndDateField(errors, control, mode),
        size: 6,
        id: 'endDate',
      },
    ];
  }

  const elements = handleFormElements();

  return (
    <div>
      <Typography variant="h5">
        <TranslatedText textMap={Translations.header} />
      </Typography>
      <div style={{ width: '100%' }}>
        <GridLayout elements={elements} />
      </div>
    </div>
  );
}

const renderVatReturnPeriod = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT'
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="vatReturnPeriodDetails_vatReturnPeriod"
      label={
        <TranslatedText textMap={Translations.vat_return_period_control} />
      }
      error={'vatReturnPeriodDetails_vatReturnPeriod' in errors}
      name="vatReturnPeriodDetails_vatReturnPeriod"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.vatReturnPeriodDetails_vatReturnPeriod &&
        errors.vatReturnPeriodDetails_vatReturnPeriod.message
      }
      disabled={true}
    />
  );
};

const renderVatReturnSubmissionDate = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT'
) => {
  return (
    <AppTextField
      variant="outlined"
      margin="normal"
      required
      fullWidth
      id="vatReturnPeriodDetails_vatReturnSubmissionDate"
      label={
        <TranslatedText
          textMap={Translations.vat_return_submission_date_control}
        />
      }
      error={'vatReturnPeriodDetails_vatReturnSubmissionDate' in errors}
      name="vatReturnPeriodDetails_vatReturnSubmissionDate"
      control={control}
      rules={{ required: 'Required Field' }}
      helperText={
        errors.vatReturnPeriodDetails_vatReturnSubmissionDate &&
        errors.vatReturnPeriodDetails_vatReturnSubmissionDate.message
      }
      disabled={true}
    />
  );
};

const renderStartDateField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT'
) => {
  return (
    <AppDatePicker
      fullWidth
      margin="normal"
      label={<TranslatedText textMap={Translations.start_date_control} />}
      autoOk
      error={'vatReturnPeriodDetails_startDate' in errors}
      control={control}
      rules={{}}
      name="vatReturnPeriodDetails_startDate"
      helperText={
        errors.vatReturnPeriodDetails_startDate &&
        errors.vatReturnPeriodDetails_startDate.message
      }
      // disabled={mode === 'VIEW'}
      disabled={true}
    />
  );
};

const renderEndDateField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>,
  mode: 'VIEW' | 'EDIT'
) => {
  return (
    <AppDatePicker
      fullWidth
      margin="normal"
      label={<TranslatedText textMap={Translations.end_date_control} />}
      autoOk
      error={'vatReturnPeriodDetails_endDate' in errors}
      control={control}
      rules={{}}
      name="vatReturnPeriodDetails_endDate"
      helperText={
        errors.vatReturnPeriodDetails_endDate &&
        errors.vatReturnPeriodDetails_endDate.message
      }
      // disabled={mode === 'VIEW'}
      disabled={true}
    />
  );
};
